import React, { useState } from "react";
import { Skeleton, Spinner } from "../../Components/Common/LoadingStates";
import {
  getEventHealthSummary,
  getEventHealthErrors,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest } from "../../helpers/cachedFetch";

const WINDOWS = [
  { id: 7,  label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
];

function fmtNum(n) {
  return Number(n || 0).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    if (ms < 60_000) return "just now";
    if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 24 * 60 * 60_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    if (ms < 30 * 24 * 60 * 60_000) return `${Math.floor(ms / 86_400_000)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return String(iso);
  }
}

/**
 * Sparkline of `processed` counts over the daily_series. Used for at-a-glance
 * trend visualization — tiny SVG, no external dep.
 */
function Sparkline({ series, color = "var(--bl-accent)", flagged = false }) {
  if (!series || series.length === 0) return null;
  const w = 200, h = 40;
  const data = series.map((s) => Number(s.processed) || 0);
  const max = Math.max(1, ...data);
  const step = w / Math.max(data.length - 1, 1);
  const points = data
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`)
    .join(" ");
  const fill = `0,${h} ${points} ${w},${h}`;
  const stroke = flagged ? "#f3af4c" : color;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 40 }} preserveAspectRatio="none">
      <polygon points={fill} fill={`rgba(${flagged ? "243,175,76" : "123,124,255"}, 0.18)`} />
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StreamCard({ stream }) {
  const flagged = stream.anomaly?.flagged;
  return (
    <div className="bl-glass" style={{
      padding: "1.1rem 1.25rem",
      border: flagged ? "1px solid rgba(243,175,76,0.5)" : undefined,
      position: "relative",
    }}>
      {flagged && (
        <div title="Today's volume is < 50% of recent average — this stream may be broken" style={{
          position: "absolute", top: 10, right: 12,
          fontSize: 10, padding: "2px 8px", borderRadius: 6,
          background: "rgba(243,175,76,0.16)", color: "#f3af4c", fontWeight: 600,
        }}>
          ⚠ Anomaly
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{stream.label}</div>
          <div style={{ fontSize: 10, color: "var(--bl-text-soft)", fontFamily: "ui-monospace, monospace" }}>{stream.event_type}</div>
        </div>
        {!stream.tracked && (
          <span style={{ fontSize: 10, color: "var(--bl-text-soft)", padding: "2px 8px", borderRadius: 6, background: "rgba(255,255,255,0.04)" }}>
            unrecognized
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 16, marginTop: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 9, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>24h</div>
          <div style={{ fontSize: 18, color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtNum(stream.count_24h)}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>7d</div>
          <div style={{ fontSize: 18, color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtNum(stream.count_7d)}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Avg/day</div>
          <div style={{ fontSize: 18, color: "var(--bl-text-soft)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtNum(stream.anomaly?.avg_per_day || 0)}</div>
        </div>
      </div>

      <Sparkline series={stream.daily_series} flagged={flagged} />

      <div style={{ marginTop: 6, fontSize: 11, color: "var(--bl-text-soft)" }}>
        Last received: <span style={{ color: stream.last_received_at ? "#fff" : "#f3af4c" }}>{fmtDate(stream.last_received_at)}</span>
        {stream.last_error && (
          <span title={stream.last_error.error_message || ""} style={{ marginLeft: 12, color: "#ff9e9e" }}>
            · last error {fmtDate(stream.last_error.received_at)}
          </span>
        )}
      </div>

      {stream.posthog && (
        <div style={{
          marginTop: 10, paddingTop: 10,
          borderTop: "1px dashed var(--bl-line)",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 9, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>PostHog</span>
          <span style={{
            fontSize: 13, fontWeight: 700,
            color: stream.posthog.has_data ? "#5be58a" : "var(--bl-text-soft)",
            fontVariantNumeric: "tabular-nums",
          }}>
            {fmtNum(stream.posthog.count)} {stream.posthog.has_data ? "captured" : "events"}
          </span>
          {stream.posthog.has_data && stream.posthog.first_event_at && (
            <span style={{ fontSize: 10, color: "var(--bl-text-soft)" }}>
              since {fmtDate(stream.posthog.first_event_at)}
            </span>
          )}
          {!stream.posthog.has_data && (
            <span style={{ fontSize: 10, color: "var(--bl-text-soft)", fontStyle: "italic" }}>
              not capturing yet ({stream.posthog.event_name})
            </span>
          )}
          {stream.posthog.last_sync_error && (
            <span title={stream.posthog.last_sync_error} style={{ fontSize: 10, color: "#ff9e9e" }}>
              · sync error
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const th = {
  textAlign: "left", padding: "0.65rem 1rem",
  fontSize: 11, fontWeight: 600,
  color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em",
  whiteSpace: "nowrap",
};
const td = { padding: "0.7rem 1rem", verticalAlign: "middle" };

const DataIngestionPanel = () => {
  const [days, setDays] = useState(30);

  const summaryReq = useCachedRequest({
    key: `data-health:summary:${days}`,
    fetcher: () => getEventHealthSummary(days).then((res) => res || {}),
    deps: [days],
  });
  const data = summaryReq.data;
  const loading = summaryReq.loading;
  const error = summaryReq.error ? (typeof summaryReq.error === "string" ? summaryReq.error : summaryReq.error?.message || "Failed to load") : "";

  const errorsReq = useCachedRequest({
    key: "data-health:errors:20",
    fetcher: () => getEventHealthErrors({ limit: 20 })
      .then((res) => res || {})
      .catch(() => ({ rows: [] })),
  });
  const errors = Array.isArray(errorsReq.data?.rows) ? errorsReq.data.rows : [];
  const errorsLoading = errorsReq.loading;

  const types = data?.types || [];
  const flaggedCount = types.filter((t) => t.anomaly?.flagged).length;

  return (
    <div>
      <div style={{ marginBottom: "1rem", fontSize: 13, color: "var(--bl-text-soft)" }}>
        Ingestion volume for every event type the mobile app pings.
        {flaggedCount > 0 && (
          <span style={{ marginLeft: 8, color: "#f3af4c", fontWeight: 600 }}>
            · {flaggedCount} stream{flaggedCount === 1 ? "" : "s"} flagged for anomaly
          </span>
        )}
      </div>

      {error && (
        <div style={{
          marginBottom: "1rem", padding: "0.75rem 1rem", borderRadius: 8,
          background: "rgba(255,110,110,0.1)", border: "1px solid rgba(255,110,110,0.3)",
          color: "#ff9e9e", fontSize: 13,
        }}>{error}</div>
      )}

      {/* ── Window selector ─────────────────────────────────────────────── */}
      <div className="bl-glass" style={{ padding: "0.85rem 1rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Window:</span>
        <div role="tablist" style={{ display: "inline-flex", background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)", borderRadius: 8, padding: 2 }}>
          {WINDOWS.map((w) => {
            const active = w.id === days;
            return (
              <button
                key={w.id}
                onClick={() => setDays(w.id)}
                style={{
                  border: "none",
                  background: active ? "var(--bl-accent)" : "transparent",
                  color: active ? "#fff" : "var(--bl-text-soft)",
                  fontSize: 12, fontWeight: 600,
                  padding: "6px 12px", borderRadius: 6,
                  cursor: "pointer",
                }}
              >{w.label}</button>
            );
          })}
        </div>
        {loading && <Spinner size={14} />}
      </div>

      {/* ── Stream grid ─────────────────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}>
        {loading && !data && (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className="bl-glass" style={{ padding: "1.1rem 1.25rem" }}>
              <Skeleton height={20} width="40%" />
              <div style={{ marginTop: 12 }}><Skeleton height={60} /></div>
            </div>
          ))
        )}
        {!loading && types.length === 0 && (
          <div className="bl-glass" style={{ padding: "2rem", gridColumn: "1 / -1", textAlign: "center", color: "var(--bl-text-soft)" }}>
            No ingestion events have been recorded yet. Streams will appear here as the mobile app pings each event type.
          </div>
        )}
        {types.map((t) => (
          <StreamCard key={t.event_type} stream={t} />
        ))}
      </div>

      {/* ── Recent errors ──────────────────────────────────────────────── */}
      {errors.length > 0 && (
        <div className="bl-glass" style={{ overflow: "hidden" }}>
          <div style={{ padding: "0.85rem 1rem", borderBottom: "1px solid var(--bl-line)" }}>
            <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1rem", fontWeight: 600 }}>Recent ingestion errors</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                  <th style={th}>When</th>
                  <th style={th}>Event</th>
                  <th style={th}>Error</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((row) => (
                  <tr key={row.id} style={{ borderTop: "1px solid var(--bl-line)" }}>
                    <td style={td}><span style={{ color: "#fff", fontVariantNumeric: "tabular-nums" }}>{new Date(row.received_at).toLocaleString()}</span></td>
                    <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{row.event_type}</td>
                    <td style={{ ...td, color: "#ff9e9e" }}>{row.error_message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!errorsLoading && errors.length === 0 && (
        <div className="bl-glass" style={{ padding: "1rem 1.25rem", color: "var(--bl-text-soft)", fontSize: 13 }}>
          No ingestion errors recorded — every ping that arrived was processed cleanly.
        </div>
      )}
    </div>
  );
};

export default DataIngestionPanel;
